import { useEffect, useRef, useState } from "react";
import { Alert, Button, Checkbox, Dialog, Inline, Stack, TextAreaField } from "@leylion/design-system";
import { createIdempotencyKey, request } from "../api/client";
import type { OrderWorkflow } from "../api/cashier-types";
import { formatMoneyMinor } from "./cashier-money";
import { valueError } from "../shared/value-copy";

type Preview = {
  orderId: string; orderVersion: number; currency: string;
  beforeTotalMinor: number; totalMinor: number; beforeTaxMinor: number; taxMinor: number;
  amountPaidMinor: number; balanceDueMinor: number; removedCouponCode: string | null;
  removedPromotionLineCount: number; canApply: boolean; resolutionCode: string | null; reviewToken: string;
};
type Apply = { expectedOrderVersion: number; removeCoupon: boolean; removeInvalidPromotions: boolean; reviewToken: string; reason: string };

export function CashierPricingRecoveryDialog({ workflow, language, couponOnly = false, onClose, onApplied, onSessionExpired }: {
  workflow: OrderWorkflow; language: "ar" | "en"; onClose: () => void;
  couponOnly?: boolean;
  onApplied: (updated: OrderWorkflow) => void; onSessionExpired: () => void;
}) {
  const ar = language === "ar";
  const [removeCoupon, setRemoveCoupon] = useState(Boolean(workflow.appliedCoupon));
  const [removeInvalidPromotions, setRemoveInvalidPromotions] = useState(!couponOnly);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const generation = useRef(0);
  const running = useRef(false);
  const attempt = useRef<{ key: string; body: Apply } | null>(null);
  const money = (value: number) => formatMoneyMinor(value, workflow.order.currency, ar ? "ar-AE" : "en-AE");
  useEffect(() => {
    generation.current++; setPreview(null); setError(""); setUncertain(false); attempt.current = null;
    return () => { generation.current++; };
  }, [workflow.order.id, workflow.order.version, removeCoupon, removeInvalidPromotions]);
  const failure = (caught: unknown) => {
    setError(valueError(caught, language));
    if ((caught as { status?: number })?.status === 401) onSessionExpired();
  };
  const loadPreview = async () => {
    if (running.current) return;
    running.current = true; setBusy(true); setError(""); setPreview(null);
    const current = generation.current;
    try {
      const result = await request<Preview>(`/api/v1/cashier/orders/${workflow.order.id}/pricing-recovery/preview`, {
        method: "POST", body: JSON.stringify({ expectedOrderVersion: workflow.order.version, removeCoupon, removeInvalidPromotions })
      });
      if (current === generation.current) setPreview(result);
    } catch (caught) { if (current === generation.current) failure(caught); }
    finally { running.current = false; if (current === generation.current) setBusy(false); }
  };
  const apply = async () => {
    if (running.current || !preview?.canApply || !reason.trim()) return;
    const current = generation.current;
    attempt.current ??= { key: createIdempotencyKey(), body: {
      expectedOrderVersion: preview.orderVersion, removeCoupon, removeInvalidPromotions,
      reviewToken: preview.reviewToken, reason: reason.trim()
    } };
    running.current = true; setBusy(true); setError("");
    try {
      const updated = await request<OrderWorkflow>(`/api/v1/cashier/orders/${workflow.order.id}/pricing-recovery/apply`, {
        method: "POST", headers: { "Idempotency-Key": attempt.current.key }, body: JSON.stringify(attempt.current.body)
      });
      if (current === generation.current) onApplied(updated);
    } catch (caught) {
      if (current !== generation.current) return;
      const status = (caught as { status?: number })?.status;
      if (status && status >= 400 && status < 500) { attempt.current = null; setUncertain(false); setPreview(null); }
      else setUncertain(true);
      failure(caught);
    } finally { running.current = false; if (current === generation.current) setBusy(false); }
  };
  return <Dialog open onClose={() => { if (!busy) onClose(); }} title={ar ? "مراجعة التسعير — المدير" : "Manager pricing review"}
    footer={<Inline>
      <Button variant="secondary" disabled={busy} onClick={onClose}>{ar ? "إلغاء" : "Cancel"}</Button>
      <Button data-testid="pricing-recovery-preview" variant="secondary" disabled={busy || uncertain} onClick={() => void loadPreview()}>{ar ? "معاينة المبالغ" : "Preview amounts"}</Button>
      <Button data-testid="pricing-recovery-apply" disabled={busy || !preview?.canApply || !reason.trim()} onClick={() => void apply()}>{uncertain ? (ar ? "التحقق وإعادة المحاولة" : "Verify and retry") : (ar ? "تأكيد إعادة التسعير" : "Confirm repricing")}</Button>
    </Inline>}>
    <Stack>
      <p>{couponOnly
        ? (ar ? "تزيل هذه المراجعة قسيمة الطلب المستقبلي فقط، وتحافظ على الأصناف وأسعارها والعروض والخصم اليدوي والدفعات. راجع المبالغ قبل التأكيد." : "This review only removes the future-order coupon, preserving items, original prices, promotions, manual discount and payments. Review the amounts before confirming.")
        : (ar ? "تزيل هذه المراجعة الخصومات المحددة أدناه، وتحافظ على أسعار الأصناف الأصلية والخصم اليدوي والدفعات المسجلة. راجع المبالغ قبل التأكيد." : "This review removes the selected discounts and preserves original item prices, the manual discount and recorded payments. Review the amounts before confirming.")}</p>
      <Checkbox data-testid="pricing-recovery-coupon" label={ar ? "إزالة القسيمة المطبقة" : "Remove the applied coupon"} checked={removeCoupon} disabled={busy || uncertain || !workflow.appliedCoupon} onChange={e => setRemoveCoupon(e.target.checked)} />
      {!couponOnly ? <Checkbox data-testid="pricing-recovery-promotions" label={ar ? "إزالة العروض المنتهية أو غير المتاحة فقط" : "Remove only expired or unavailable promotions"} checked={removeInvalidPromotions} disabled={busy || uncertain} onChange={e => setRemoveInvalidPromotions(e.target.checked)} /> : null}
      {error ? <Alert tone="danger" title={ar ? "تعذر إتمام المراجعة" : "Review could not complete"}><span role="alert">{error}</span></Alert> : null}
      {uncertain ? <Alert tone="warning" title={ar ? "نتيجة غير مؤكدة" : "Result not confirmed"}>{ar ? "أعد المحاولة للتحقق من نفس العملية. ستُستخدم بيانات التأكيد ومرجع العملية نفسيهما." : "Retry to verify the same operation. The confirmation data and operation reference will be reused."}</Alert> : null}
      {preview ? <Stack>
        {preview.removedCouponCode ? <p>{ar ? "القسيمة التي ستُزال: " : "Coupon to remove: "}<bdi>{preview.removedCouponCode}</bdi></p> : null}
        {!couponOnly ? <p>{ar ? "عدد أسطر العروض التي ستُزال: " : "Promotion lines to remove: "}{preview.removedPromotionLineCount}</p> : null}
        <div data-testid="pricing-recovery-amounts">
          <p>{ar ? "الإجمالي السابق: " : "Previous total: "}{money(preview.beforeTotalMinor)}</p>
          <p>{ar ? "الإجمالي الجديد: " : "New total: "}<strong>{money(preview.totalMinor)}</strong></p>
          <p>{ar ? "الضريبة السابقة: " : "Previous tax: "}{money(preview.beforeTaxMinor)}</p>
          <p>{ar ? "الضريبة الجديدة: " : "New tax: "}{money(preview.taxMinor)}</p>
          <p>{ar ? "المدفوع المحفوظ: " : "Preserved payments: "}{money(preview.amountPaidMinor)}</p>
          <p>{ar ? "المتبقي الجديد: " : "New balance due: "}<strong>{money(preview.balanceDueMinor)}</strong></p>
        </div>
        {preview.resolutionCode ? <Alert tone="warning" title={ar ? "مراجعة مطلوبة" : "Review required"}>{valueError({ problem: { title: preview.resolutionCode } }, language)}</Alert> : null}
      </Stack> : null}
      <TextAreaField data-testid="pricing-recovery-reason" label={ar ? "سبب مراجعة المدير" : "Manager review reason"} maxLength={500} value={reason} disabled={busy || uncertain} onChange={e => setReason(e.target.value)} />
    </Stack>
  </Dialog>;
}
